// Pick-winner modal: list of every answered bid (across every carrier
// with at least one quote) with a cheapest tag. Tapping a row selects
// the bid; the primary button calls `pickWinner` with the bid id.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyRates } from '@magimp/shared-hooks';
import { Button, Icon, Modal } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { usePickWinner } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import { carrierMarkText } from '../../carrierMark.js';
import { formatMoney } from '../../format.js';
import { aggregateProductQuantity, computeBidTotal, getCheapestBid } from '../../pricing.js';
import type { BidUnit, CarrierBid, CarrierBidQuote, RequestView, UserRef } from '../../types.js';

// WHY (#1060): keep the pick-winner row visually consistent with
//   `CarrierBidCard` — same localized unit suffix and same single/range
//   day display. Inlining the keys here avoids re-exporting helpers
//   from the card module and keeps each component self-contained.
type UnitSuffixKey =
  | 'requests:carriers.unitSuffix.kg'
  | 'requests:carriers.unitSuffix.m3'
  | 'requests:carriers.unitSuffix.piece';

function unitSuffixKey(unit: BidUnit): UnitSuffixKey {
  if (unit === 'kg') return 'requests:carriers.unitSuffix.kg';
  if (unit === 'm3') return 'requests:carriers.unitSuffix.m3';
  return 'requests:carriers.unitSuffix.piece';
}

export interface PickWinnerModalProps {
  request: RequestView;
  me: UserRef;
}

interface Row {
  carrier: CarrierBid;
  bid: CarrierBidQuote;
}

export function PickWinnerModal({ request }: PickWinnerModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [picked, setPicked] = useState<string | null>(request.winnerBidId);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const setModal = useRequestsStore((s) => s.setModal);
  const pickWinnerMutation = usePickWinner(request.id);
  const onClose = () => setModal(null);

  async function onConfirm(): Promise<void> {
    if (!picked) return;
    setSubmitError(null);
    try {
      await pickWinnerMutation.mutateAsync(picked);
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  const { data: fx } = useCurrencyRates();
  const fxRates = fx?.rates ?? {};
  const rows: Row[] = [];
  for (const carrier of request.carriers) {
    if (carrier.state !== 'answered') continue;
    for (const bid of carrier.bids) {
      // INVARIANT (#703): only primary (`scope === 'REQUEST'`) bids
      //   are eligible winners on the wire. Filtering here keeps the
      //   UI consistent with `RequestWinnerService.pickWinner` and the
      //   SUCCESS-transition gate so a logist cannot select an
      //   override and watch it bounce server-side.
      if (bid.scope !== 'REQUEST') continue;
      rows.push({ carrier, bid });
    }
  }

  // WHY (#1060): piece bids price against aggregated SKU count
  //   (`items[].productQuantity`). Without this, `getCheapestBid` would
  //   silently drop piece bids in the modal even when `CarriersTab`
  //   highlights one as cheapest — divergent guidance at the
  //   winner-selection step.
  const requestProductQuantity = aggregateProductQuantity(request.items);
  const pricingRequest = {
    weight: request.weight,
    volume: request.volume,
    productQuantity: requestProductQuantity,
  };
  const cheapest = getCheapestBid(
    request.carriers.filter((c) => c.state === 'answered'),
    pricingRequest,
    fxRates,
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:pickWinnerModal.title', { id: request.id })}
      width={640}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            icon="crown"
            disabled={!picked || pickWinnerMutation.isPending}
            onClick={() => {
              void onConfirm();
            }}
          >
            {t('requests:pickWinnerModal.confirm')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(({ carrier, bid }) => {
          const tags: { icon: 'badge-dollar-sign'; label: string; color: string }[] = [];
          if (cheapest !== null && cheapest.bid.id === bid.id) {
            tags.push({
              icon: 'badge-dollar-sign',
              label: t('requests:carriers.cheapest'),
              color: 'var(--accent-1-press)',
            });
          }
          const sel = picked === bid.id;
          const totals = computeBidTotal(bid, pricingRequest, fxRates);
          return (
            <button
              key={bid.id}
              type="button"
              onClick={() => setPicked(bid.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr 160px 1fr',
                gap: 12,
                alignItems: 'center',
                padding: '12px',
                borderRadius: 'var(--r-2)',
                textAlign: 'left',
                background: sel ? 'var(--accent-1-subtle)' : 'var(--bg-0)',
                border: `1px solid ${sel ? 'var(--accent-1)' : 'var(--border-1)'}`,
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  border: `2px solid ${sel ? 'var(--accent-1)' : 'var(--border-strong)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {sel && (
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--accent-1)',
                    }}
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--fg-1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {carrier.carrier.name}
                  <span
                    aria-hidden="true"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: 'var(--fg-3)',
                      padding: '1px 5px',
                      background: 'var(--bg-inset)',
                      borderRadius: 3,
                    }}
                  >
                    {carrierMarkText(carrier.carrier.name, carrier.carrier.code)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--fg-3)',
                    marginTop: 2,
                    display: 'flex',
                    gap: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  {tags.length
                    ? tags.map((tg) => (
                        <span
                          key={tg.label}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 3,
                            color: tg.color,
                            fontWeight: 600,
                          }}
                        >
                          <Icon name={tg.icon} size={10} />
                          {tg.label}
                        </span>
                      ))
                    : bid.comment}
                </div>
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  textAlign: 'right',
                  color: 'var(--fg-1)',
                }}
              >
                {formatMoney(bid.pricePerUnit, bid.currency)}
                {t(unitSuffixKey(bid.unit))}
                {totals && (
                  <div style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 400 }}>
                    {t('requests:carriers.total')}: {formatMoney(totals.total, bid.currency)}
                    {totals.usdHint !== undefined && (
                      <span style={{ marginLeft: 4 }}>
                        ≈ {formatMoney(Math.round(totals.usdHint), 'USD')}
                      </span>
                    )}
                  </div>
                )}
                {bid.daysMin != null && bid.daysMax != null && (
                  <div style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 400 }}>
                    {bid.daysMin === bid.daysMax
                      ? t('requests:carriers.daysShort', { days: bid.daysMin })
                      : t('requests:carriers.daysShortRange', {
                          min: bid.daysMin,
                          max: bid.daysMax,
                        })}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{bid.comment || '-'}</div>
            </button>
          );
        })}
        {!rows.length && (
          <div
            style={{ fontSize: 13, color: 'var(--fg-3)', padding: '24px 0', textAlign: 'center' }}
          >
            {t('requests:pickWinnerModal.empty')}
          </div>
        )}
      </div>
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}
